import { Box, useTheme } from "@mui/material";

import { ResponsiveLine, Serie } from "@nivo/line";
import dayjs from "dayjs";
import { useMemo } from "react";
import { DataSeriesUnit, SenecBaseTheme } from "src/newsenec-ui-components";
import { ChartGrid } from "src/components/monitoring/axisLegends/ChartGrid";
import { LineBottomAxis } from "src/components/monitoring/axisLegends/LineBottomAxis";
import { YAxisLegend } from "src/components/monitoring/axisLegends/YAxisLegend";
import { Lines } from "src/components/monitoring/senec-state-history-chart/Lines";
import StateChartTooltip from "src/components/monitoring/senec-state-history-chart/StateChartTooltip";
import {
    getDynamicHeightOffset,
    getLineChartTheme,
    transformDataToSeries,
} from "src/components/monitoring/senec-state-history-chart/stateHistoryChartUtils";
import { SenecStateHistoryChartProps, TooltipProps } from "src/components/monitoring/senec-state-history-chart/stateHistoryTypes";

const DEFAULT_PRECISION = "day";
const CHART_HEIGHT = 337.5;
const BOX_HEIGHT = 346;

export const SenecStateHistoryChart = <T extends string>({
    data = [],
    precision = DEFAULT_PRECISION,
    onLineClick = () => null,
    numberOfXLabels = 12,
    locale,
}: SenecStateHistoryChartProps<T>) => {
    if (data.length < 1) return;
    const theme: SenecBaseTheme = useTheme();
    const sortedData = [...data].sort((a, b) => a.from.getTime() - b.from.getTime());
    const uniqueStatesTranslated = [...Array.from(new Set(data.map((entry) => entry.translation))), ""];
    const minTime = sortedData.at(0)!.from;
    const maxTime = sortedData.at(-1)!.to;
    const dataAsSerie: Serie[] = transformDataToSeries(sortedData, uniqueStatesTranslated, minTime, maxTime);
    // Dates weren't properly showing the milliseconds, the data was manipulated at some point
    const dataSeriesCopy = useMemo(() => structuredClone(dataAsSerie), [dataAsSerie]);

    const dynamicHeightOffset = getDynamicHeightOffset(uniqueStatesTranslated.length);
    const height = CHART_HEIGHT + dynamicHeightOffset;
    const boxHeight = BOX_HEIGHT + dynamicHeightOffset;
    return (
        <Box sx={{ position: "relative", height: boxHeight, overflow: "visible" }}>
            <Box
                sx={{
                    position: "absolute",
                    height: boxHeight,
                    left: "16px",
                    right: "16px",
                    zIndex: 20,
                    overflow: "visible",
                }}
            >
                <ResponsiveLine
                    data={dataAsSerie}
                    theme={getLineChartTheme(theme)}
                    margin={{ top: 9, right: 0, bottom: 0, left: 1 }}
                    xScale={{
                        type: "time",
                        precision,
                        min: minTime,
                        max: maxTime,
                    }}
                    yScale={{
                        type: "point",
                    }}
                    enablePoints={false}
                    enableGridX={false}
                    enableGridY={true}
                    lineWidth={10}
                    colors={{ datum: "color" }}
                    layers={[(data) => <Lines serieData={dataSeriesCopy} lineChartData={data} onLineClick={onLineClick} />]}
                    // @ts-expect-error using custom tooltip props here - if you know how to type this correctly without affecting other components, please do
                    tooltip={(data: TooltipProps) => <StateChartTooltip {...data} locale={locale} />}
                />
            </Box>
            <YAxisLegend
                variant={"left"}
                chartType={"line"}
                yLegendValues={uniqueStatesTranslated}
                unit={DataSeriesUnit.STATE}
                height={height + 4}
                typographyStyleOverrides={{ pointerEvents: "none" }}
            />
            <ChartGrid amountLines={uniqueStatesTranslated.length} height={height} />

            <LineBottomAxis
                timeframe={{
                    start: dayjs(minTime),
                    end: dayjs(maxTime),
                }}
                locale={locale}
                numberOfXLabels={numberOfXLabels}
                largerGap={false}
            />
            <div id="tooltip-portal" />
        </Box>
    );
};
